/** Tenký klient nad REST API. Cookie se posílá automaticky, token se nikde nedrží. */

export type Role = 'admin' | 'coach' | 'client'

export interface User {
  id: number
  email: string
  name: string
  role: Role
  coachId: number | null
  active: boolean
  note: string | null
  createdAt: string
}

export interface Program {
  id: number
  name: string
  description: string | null
  gameId: string
  params: Record<string, number | boolean | string>
  ownerId: number | null
  ownerName: string | null
  createdAt: string
  updatedAt: string
}

export interface Assignment {
  id: number
  userId: number
  programId: number
  assignedBy: number | null
  active: boolean
  targetSessions: number | null
  dueDate: string | null
  note: string | null
  sortOrder: number
  createdAt: string
  program: {
    id: number
    name: string
    description: string | null
    gameId: string
    params: Record<string, number | boolean | string>
  } | null
  completedSessions: number
  lastScore: number | null
  lastPlayedAt: string | null
}

export interface TrainingSession {
  id: number
  userId: number
  programId: number | null
  gameId: string
  params: Record<string, number | boolean | string>
  score: number
  accuracy: number
  metrics: Record<string, number>
  trialCount: number
  durationMs: number
  seed: number | null
  completed: boolean
  finishedAt: string
  userName: string | null
  programName: string | null
}

export interface Trial {
  index: number
  difficulty: number
  correct: boolean
  hits: number
  targets: number
  responseMs: number
}

export interface ProgressPoint {
  id: number
  score: number
  accuracy: number
  finishedAt: string
  programId: number | null
  programName: string | null
}

export interface DailyProgress {
  day: string
  sessions: number
  avgScore: number
  bestScore: number
  avgAccuracy: number
}

export interface GameSummary {
  gameId: string
  sessions: number
  bestScore: number
  firstScore: number
  lastScore: number
  lastPlayedAt: string
  totalMs: number
}

/** Chyba z API i sítě. `status` 0 znamená, že se požadavek vůbec neodeslal. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    })
  } catch {
    throw new ApiError(0, 'Server je nedostupný. Zkontroluj připojení.')
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const data: unknown = text ? safeParse(text) : null

  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error
        : null) ?? `Chyba ${response.status}`
    throw new ApiError(response.status, message)
  }

  return data as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const get = <T,>(path: string): Promise<T> => request<T>(path)
const post = <T,>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
const patch = <T,>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
const del = <T,>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' })

/** Sestaví query string a vynechá nevyplněné hodnoty. */
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

export const api = {
  auth: {
    login: (email: string, password: string) => post<{ user: User }>('/auth/login', { email, password }),
    logout: () => post<{ ok: true }>('/auth/logout'),
    me: () => get<{ user: User }>('/auth/me'),
    changePassword: (currentPassword: string, newPassword: string) =>
      post<{ ok: true }>('/auth/password', { currentPassword, newPassword }),
  },

  users: {
    list: (role?: Role) => get<{ users: User[] }>(`/users${qs({ role })}`),
    coaches: () => get<{ users: User[] }>('/users/coaches'),
    get: (id: number) => get<{ user: User }>(`/users/${id}`),
    create: (data: {
      email: string
      name: string
      password: string
      role?: Role
      coachId?: number | null
      note?: string | null
    }) => post<{ user: User }>('/users', data),
    update: (id: number, data: Partial<Omit<User, 'id' | 'createdAt'>>) =>
      patch<{ user: User }>(`/users/${id}`, data),
    setPassword: (id: number, newPassword: string) =>
      post<{ ok: true }>(`/users/${id}/password`, { newPassword }),
    remove: (id: number) => del<{ ok: true }>(`/users/${id}`),
  },

  programs: {
    list: (gameId?: string) => get<{ programs: Program[] }>(`/programs${qs({ gameId })}`),
    get: (id: number) => get<{ program: Program }>(`/programs/${id}`),
    create: (data: {
      name: string
      description?: string | null
      gameId: string
      params: Record<string, number | boolean | string>
    }) => post<{ program: Program }>('/programs', data),
    update: (
      id: number,
      data: Partial<{
        name: string
        description: string | null
        gameId: string
        params: Record<string, number | boolean | string>
      }>,
    ) => patch<{ program: Program }>(`/programs/${id}`, data),
    remove: (id: number) => del<{ ok: true }>(`/programs/${id}`),
  },

  assignments: {
    list: (userId?: number, activeOnly?: boolean) =>
      get<{ assignments: Assignment[] }>(`/assignments${qs({ userId, activeOnly })}`),
    get: (id: number) => get<{ assignment: Assignment }>(`/assignments/${id}`),
    create: (data: {
      userId: number
      programId: number
      targetSessions?: number | null
      dueDate?: string | null
      note?: string | null
      sortOrder?: number
    }) => post<{ assignment: Assignment }>('/assignments', data),
    update: (
      id: number,
      data: Partial<{
        targetSessions: number | null
        dueDate: string | null
        note: string | null
        sortOrder: number
        active: boolean
      }>,
    ) => patch<{ assignment: Assignment }>(`/assignments/${id}`, data),
    remove: (id: number) => del<{ ok: true }>(`/assignments/${id}`),
  },

  sessions: {
    create: (data: {
      programId?: number | null
      gameId: string
      params: Record<string, number | boolean | string>
      score: number
      accuracy: number
      metrics: Record<string, number>
      trials: Trial[]
      durationMs: number
      seed?: number | null
      completed?: boolean
    }) => post<{ session: TrainingSession }>('/sessions', data),

    list: (filter: { userId?: number; gameId?: string; programId?: number; limit?: number } = {}) =>
      get<{ sessions: TrainingSession[] }>(`/sessions${qs(filter)}`),

    get: (id: number) => get<{ session: TrainingSession; trials: Trial[] }>(`/sessions/${id}`),

    progress: (gameId: string, userId?: number, days?: number) =>
      get<{ points: ProgressPoint[]; daily: DailyProgress[] }>(
        `/sessions/progress${qs({ gameId, userId, days })}`,
      ),

    summary: (userId?: number) => get<{ summary: GameSummary[] }>(`/sessions/summary${qs({ userId })}`),
  },
}
